
import { AxiosHeaders } from "axios";
import { baseService } from "./base.service";
import { DashboardResponse } from "./query/dashboard.query";
import type { DocumentApiResponse } from "@/core/types/documents";

type DocumentsResponseData = {
    documents: DocumentApiResponse[];
};

// Master API calls
export async function getMasterAPI(docType: string, params?: string) {
    const endPoint = `/master/list/${docType}${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getMasterListRowCountAPI(docType: string, params?: string) {
    const endPoint = `/master/rowCount/${docType}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

// Master Settings API calls
export async function getMasterSettingsAPI(docType: string, params?: string) {
    const endPoint = `/master/settings/${docType}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

// Dropdown API calls
export async function dropDownListApi<T = any>(docType: string, params?: string) {
    const endPoint = `/dropdown/${docType}/${params ? '?' + params : ''}`;
    return await baseService<T>({ method: 'GET', endPoint });
}




// Form API calls
export async function getFormApi(docType: string | null, docNo: string, params?: string): Promise<any> {
    if (!docNo) return;
    const endPoint = `/form/${docType}/${docNo}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function saveFormApi(data: any, docType: string, params?: string, headers?: AxiosHeaders) {
    const endPoint = `/form/${docType}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'POST', endPoint, data, headers });
}

export async function updateFormApi(data: any, docType: string, docNo: string, params?: string, headers?: AxiosHeaders): Promise<any> {
    const endPoint = `/form/${docType}/${docNo}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'PUT', endPoint, data, headers });
}

export async function deleteFormApi(docType?: string, docNo?: string, params?: string) {
    const endPoint = `/form/${docType}/${docNo}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'DELETE', endPoint });
}



export async function getMenusApi() {
    const endPoint = `/auth/menus/`;
    return await baseService({ method: 'GET', endPoint });
}




export async function rolePermissionList(docType: string, params?: string) {
    const endPoint = `/dropdown/${docType}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}



export async function isValueExistsApi(docType: string, column: string, value: string, docNo: string) {
    const endPoint = `/isValueExists/${docType}/${docNo ? docNo + '/' : ""}`;
    return await baseService({ method: 'POST', endPoint, data: { column, value } });
}

export async function userApi(method: 'GET' | 'POST' | 'PUT', docType: string, data?: any, docNo?: string, params?: string): Promise<any> {
    let endPoint = `/form/${docType}/`;
    if (docNo) {
        endPoint += `${docNo}/`;
    }
    if (params) {
        endPoint += `?${params}`;
    }
    return await baseService({ method, endPoint, data });
}




// Documents API
export async function getDocumentsApi(params?: string) {
    const endPoint = `/auth/documents/${params ? '?' + params : ''}`;
    return await baseService<DocumentsResponseData>({ method: 'GET', endPoint });
}


export async function getDocumentPrintApi(docType: string, docSrNo: number | null, params?: string) {
    const responseType = 'arraybuffer';
    const endPoint = `/master/print/${docType}/${docSrNo}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint, responseType });
}

export async function getDashboardData() {
    const endPoint = `/dashboard/DB/CARD/`;
    const result = await baseService({ method: 'GET', endPoint });
    return result?.data as DashboardResponse;
}


export async function getServerHealthApi() {
    const endPoint = `/health/`;
    return await baseService({ method: 'GET', endPoint });
}




export async function approvalApi(data: any, docType: string, docNo: string, params?: string, headers?: AxiosHeaders): Promise<any> {
    const endPoint = `/approval/${docType}/${docNo}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'PUT', endPoint, data, headers });
}



export async function assignApi(data: any, docType: string, docNo: string, params?: string, headers?: AxiosHeaders): Promise<any> {
    const endPoint = `/assign/${docType}/${docNo}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'PUT', endPoint, data, headers });
}





export async function getMasterListFiltersApi(docType: string, params?: any,) {
    const endPoint = `/master/filter/header/${docType}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getMasterListFiltersDataApi(docType: string, srNo?: number, params?: any,) {
    const endPoint = `/master/filter/select/${docType}/${srNo}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}



export async function getCompanySettingsApi() {
    const endPoint = `/companySettings/`;
    return await baseService({ method: 'GET', endPoint });
}

// User-specific master list view (column layout, visibility, sizing, etc.)
export async function saveMasterViewApi(docType: string, data: any, params?: string) {
    const endPoint = `/master/view/${docType}/${params ? '?' + params : ''}`;
    return await baseService({ method: 'POST', endPoint, data });
}


// Dashboard endpoints (from OpenAPI spec - used by tracking.dashboard.tsx)
export async function getTrackingDashboardSummaryApi(params?: string) {
    const endPoint = `/tracking/dashboard/summary/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getTrackingDashboardActivityTimelineApi(params?: string) {
    const endPoint = `/tracking/dashboard/activity-timeline/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getTrackingDashboardProductivityTrendApi(params?: string) {
    const endPoint = `/tracking/dashboard/productivity-trend/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getTrackingDashboardApplicationUsageApi(params?: string) {
    const endPoint = `/tracking/dashboard/application-usage/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getTrackingDashboardWorkSessionsApi(params?: string) {
    const endPoint = `/tracking/dashboard/work-sessions/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getTrackingDashboardIdleLogsApi(params?: string) {
    const endPoint = `/tracking/dashboard/idle-logs/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}

export async function getTrackingUserReportApi(userId: string, params?: string) {
    const endPoint = `/tracking/user/${userId}/report/${params ? '?' + params : ''}`;
    return await baseService({ method: 'GET', endPoint });
}